import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { AlertCircle, CheckCircle, Loader2, Search, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

export const Route = createFileRoute('/_protected/admin/patient-management')({
  component: PatientManagementPage,
});

// Valid options for "How did you hear about us" dropdown
const HEARD_ABOUT_US_OPTIONS = [
  '469- Existing Patient ',
  '12- Internet (e.g. Google, Website)',
  '31171-Catholic Health Services (CHS)',
  '31162-CityMD',
  '31168-Cold Spring Harbor',
  '31163-Doctor (O.D., M.D., any Physician)',
  '1-Family and Friends',
  '31683-Hospital',
  '2-Insurance Carrier',
  '31170-LIFQHC',
  '31682-Local Advertising (e.g. Saw sign, passed by, town resident)',
  '31172-Mets',
  '31680-Other',
  '403-Real Self',
  '31166-SightMD- employee',
  '31684-Social Media (e.g. Facebook, Instagram)',
  '31169-Sun River Health (Formally Hudson River Healthcare)',
  '31681-Urgent Care',
  '10010-Veterans Association (VA)',
  '31161-ZocDoc',
  'Other',
] as const;

type HeardAboutUsOption = (typeof HEARD_ABOUT_US_OPTIONS)[number];

// Form validation schemas
const patientLookupSchema = z.object({
  patientEhrId: z.string().trim().min(1, 'Patient EHR ID is required'),
});

const demographicsFormSchema = z.object({
  firstName: z.string().optional(),
  middleName: z.string().optional(),
  lastName: z.string().optional(),
  dob: z.string().optional(),
  street: z.string().optional(),
  street2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  zipExtension: z.string().optional(),
  homePhone: z.string().optional(),
  cellPhone: z.string().optional(),
  email: z.string().optional(),
  heardAboutUs: z.enum(HEARD_ABOUT_US_OPTIONS).optional(),
  heardAboutUsDescription: z.string().optional(),
});

const insuranceFormSchema = z.object({
  cases: z.array(
    z.object({
      type: z.enum(['Primary', 'Secondary', 'Tertiary']),
      caseId: z.string(),
      policyNumber: z.string(),
      groupNumber: z.string(), // Required for updates
      // Include all other fields for pass-through
      provider: z.object({
        name: z.string(),
        displayName: z.string(),
        detailedName: z.string().optional(),
        address: z.string(),
        phone: z.string(),
        ehrId: z.string(), // Required for updates
      }),
      copay: z.string(), // Required for updates
      insuranceRecordId: z.string(), // Required for updates
      policyHolderFirstName: z.string().optional(),
      policyHolderContactInfo: z
        .object({
          street1: z.string().optional(),
        })
        .optional(),
    })
  ),
});

type PatientLookupData = z.infer<typeof patientLookupSchema>;
type DemographicsFormData = z.infer<typeof demographicsFormSchema>;
type InsuranceFormData = z.infer<typeof insuranceFormSchema>;

function PatientManagementPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization } = useOrganization();
  const router = useRouter();
  const [currentPatientEhrId, setCurrentPatientEhrId] = useState<string | null>(null);
  const [patientName, setPatientName] = useState<string | null>(null);

  const currentOrganizationId = organization?.id;

  // Forms
  const lookupForm = useForm<PatientLookupData>({
    resolver: zodResolver(patientLookupSchema),
    defaultValues: {
      patientEhrId: '',
    },
  });

  const demographicsForm = useForm<DemographicsFormData>({
    resolver: zodResolver(demographicsFormSchema),
    defaultValues: {},
  });

  const insuranceForm = useForm<InsuranceFormData>({
    resolver: zodResolver(insuranceFormSchema),
    defaultValues: {
      cases: [],
    },
  });

  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // tRPC queries and mutations
  const demographicsQuery = trpc.admin.getPatientDemographics.useQuery(
    {
      organizationId: currentOrganizationId || '',
      patientEhrId: currentPatientEhrId || '',
    },
    {
      enabled: !!currentOrganizationId && !!currentPatientEhrId,
    }
  );

  // Handle demographics query success
  useEffect(() => {
    if (demographicsQuery.data) {
      const data = demographicsQuery.data;
      // Set patient name for display
      setPatientName(`${data.firstName} ${data.lastName}`);

      // Populate demographics form with current values
      demographicsForm.reset({
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        dob: data.dob,
        street: data.address.street,
        street2: data.address.street2,
        city: data.address.city,
        state: data.address.state,
        zipCode: data.address.zipCode,
        zipExtension: data.address.zipExtension,
        homePhone: data.contact.homePhone,
        cellPhone: data.contact.cellPhone,
        email: data.contact.email,
        heardAboutUs: data.heardAboutUs,
        heardAboutUsDescription: data.heardAboutUsDescription,
      });
    }
  }, [demographicsQuery.data, demographicsForm]);

  // Handle demographics query error
  useEffect(() => {
    if (demographicsQuery.error) {
      toast.error('Failed to load patient demographics', {
        description: demographicsQuery.error.message,
      });
    }
  }, [demographicsQuery.error]);

  const insuranceQuery = trpc.admin.getPatientInsurance.useQuery(
    {
      organizationId: currentOrganizationId || '',
      patientEhrId: currentPatientEhrId || '',
    },
    {
      enabled: !!currentOrganizationId && !!currentPatientEhrId,
    }
  );

  // Handle insurance query success
  useEffect(() => {
    if (insuranceQuery.data) {
      const data = insuranceQuery.data;
      // Populate insurance form with current values from EHR
      // Display actual values (including undefined as empty string) so users can see what will be submitted
      insuranceForm.reset({
        cases: data.cases.map((c): z.infer<typeof insuranceFormSchema>['cases'][number] => ({
          type: c.type,
          caseId: c.caseId,
          policyNumber: c.policyNumber,
          groupNumber: c.groupNumber || '',
          provider: {
            ...c.provider,
            ehrId: c.provider.ehrId || '',
          },
          copay: c.copay || '',
          insuranceRecordId: c.insuranceRecordId || '',
          policyHolderFirstName: c.policyHolderFirstName,
          policyHolderContactInfo: c.policyHolderContactInfo,
        })),
      });
    }
  }, [insuranceQuery.data, insuranceForm]);

  // Handle insurance query error
  useEffect(() => {
    if (insuranceQuery.error) {
      toast.error('Failed to load patient insurance', {
        description: insuranceQuery.error.message,
      });
    }
  }, [insuranceQuery.error]);

  const updateDemographicsMutation = trpc.admin.updatePatientDemographics.useMutation({
    onSuccess: (data) => {
      toast.success('Demographics updated successfully', {
        description: `Updated ${data.firstName} ${data.lastName}`,
      });
      // Refetch to get latest data
      demographicsQuery.refetch();
    },
    onError: (error) => {
      toast.error('Failed to update demographics', {
        description: error.message,
      });
    },
  });

  const updateInsuranceMutation = trpc.admin.updatePatientInsurance.useMutation({
    onSuccess: () => {
      toast.success('Insurance updated successfully');
      // Refetch to get latest data
      insuranceQuery.refetch();
    },
    onError: (error) => {
      toast.error('Failed to update insurance', {
        description: error.message,
      });
    },
  });

  const onLoadPatient = (data: PatientLookupData) => {
    setCurrentPatientEhrId(data.patientEhrId);
  };

  const onUpdateDemographics = (data: DemographicsFormData) => {
    if (!currentOrganizationId || !currentPatientEhrId) {
      toast.error('No patient loaded');
      return;
    }

    // Build updates object with properly typed fields matching UpdateDemographicsParams
    interface UpdatesPayload {
      firstName?: string;
      middleName?: string;
      lastName?: string;
      dob?: string;
      address?: {
        street?: string;
        street2?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        zipExtension?: string;
      };
      contact?: {
        homePhone?: string;
        cellPhone?: string;
        email?: string;
      };
      heardAboutUs?: HeardAboutUsOption;
      heardAboutUsDescription?: string;
    }

    const updates: UpdatesPayload = {};

    // Add basic fields
    if (data.firstName !== undefined) updates.firstName = data.firstName;
    if (data.middleName !== undefined) updates.middleName = data.middleName;
    if (data.lastName !== undefined) updates.lastName = data.lastName;
    if (data.dob !== undefined) updates.dob = data.dob;

    // Add address fields
    const address: {
      street?: string;
      street2?: string;
      city?: string;
      state?: string;
      zipCode?: string;
      zipExtension?: string;
    } = {};
    if (data.street !== undefined) address.street = data.street;
    if (data.street2 !== undefined) address.street2 = data.street2;
    if (data.city !== undefined) address.city = data.city;
    if (data.state !== undefined) address.state = data.state;
    if (data.zipCode !== undefined) address.zipCode = data.zipCode;
    if (data.zipExtension !== undefined) address.zipExtension = data.zipExtension;
    if (Object.keys(address).length > 0) updates.address = address;

    // Add contact fields
    const contact: {
      homePhone?: string;
      cellPhone?: string;
      email?: string;
    } = {};
    if (data.homePhone !== undefined) contact.homePhone = data.homePhone;
    if (data.cellPhone !== undefined) contact.cellPhone = data.cellPhone;
    if (data.email !== undefined) contact.email = data.email;
    if (Object.keys(contact).length > 0) updates.contact = contact;

    // Add "heard about us" fields
    if (data.heardAboutUs !== undefined) updates.heardAboutUs = data.heardAboutUs;
    if (data.heardAboutUsDescription !== undefined)
      updates.heardAboutUsDescription = data.heardAboutUsDescription;

    updateDemographicsMutation.mutate({
      organizationId: currentOrganizationId,
      patientEhrId: currentPatientEhrId,
      updates,
    });
  };

  const onUpdateInsurance = () => {
    if (!currentOrganizationId || !currentPatientEhrId) {
      toast.error('No patient loaded');
      return;
    }

    const formData = insuranceForm.getValues();

    updateInsuranceMutation.mutate({
      organizationId: currentOrganizationId,
      patientEhrId: currentPatientEhrId,
      insuranceCases: formData.cases,
    });
  };

  const onClearPatient = () => {
    setCurrentPatientEhrId(null);
    setPatientName(null);
    lookupForm.reset();
    demographicsForm.reset({});
    insuranceForm.reset({ cases: [] });
  };

  const isLoading = demographicsQuery.isLoading || insuranceQuery.isLoading;
  const hasPatientLoaded = !!currentPatientEhrId && !isLoading;

  if (isAdminLoading || !isSuperAdmin) {
    return null;
  }

  if (!currentOrganizationId) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-xl">No Organization Selected</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Please select an organization from the organization selector to use this tool.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="Patient Management"
      description="View and update patient demographics and insurance information"
      icon={User}
      isSuperAdminPage={true}
    >
      <div className="space-y-6">
        {/* Patient Lookup Card */}
        <Card className="border-border/50">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Patient Lookup</CardTitle>
            <CardDescription>Enter the patient's EHR ID to load their information</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...lookupForm}>
              <form onSubmit={lookupForm.handleSubmit(onLoadPatient)} className="space-y-4">
                <div className="flex gap-4">
                  <FormField
                    control={lookupForm.control}
                    name="patientEhrId"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Patient EHR ID</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter patient EHR ID"
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex items-end gap-2">
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <Search className="mr-2 h-4 w-4" />
                          Load Patient
                        </>
                      )}
                    </Button>
                    {hasPatientLoaded && (
                      <Button type="button" variant="outline" onClick={onClearPatient}>
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                {patientName && (
                  <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm font-medium text-green-700 dark:bg-green-950 dark:text-green-300">
                    <CheckCircle className="h-4 w-4" />
                    <span>Loaded: {patientName}</span>
                  </div>
                )}
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Demographics Card */}
        {hasPatientLoaded && demographicsQuery.data && (
          <Card className="border-border/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">Patient Demographics</CardTitle>
              <CardDescription>Update patient demographic information</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...demographicsForm}>
                <form
                  onSubmit={demographicsForm.handleSubmit(onUpdateDemographics)}
                  className="space-y-6"
                >
                  {/* Name Fields */}
                  <div>
                    <h3 className="mb-3 text-sm font-semibold">Name</h3>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <FormField
                        control={demographicsForm.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={demographicsForm.control}
                        name="middleName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Middle Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={demographicsForm.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Date of Birth */}
                  <FormField
                    control={demographicsForm.control}
                    name="dob"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date of Birth (MM-DD-YYYY)</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="MM-DD-YYYY" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  {/* Address Fields */}
                  <div>
                    <h3 className="mb-3 text-sm font-semibold">Address</h3>
                    <div className="grid gap-4">
                      <FormField
                        control={demographicsForm.control}
                        name="street"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Street</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={demographicsForm.control}
                        name="street2"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Street 2</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="grid gap-4 sm:grid-cols-3">
                        <FormField
                          control={demographicsForm.control}
                          name="city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>City</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={demographicsForm.control}
                          name="state"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>State</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="grid gap-4 sm:grid-cols-2">
                          <FormField
                            control={demographicsForm.control}
                            name="zipCode"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Zip Code</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={demographicsForm.control}
                            name="zipExtension"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Zip Ext</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Contact Fields */}
                  <div>
                    <h3 className="mb-3 text-sm font-semibold">Contact Information</h3>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <FormField
                        control={demographicsForm.control}
                        name="homePhone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Home Phone</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={demographicsForm.control}
                        name="cellPhone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Cell Phone</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={demographicsForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input {...field} type="email" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Heard About Us Fields */}
                  <div>
                    <h3 className="mb-3 text-sm font-semibold">Referral Source</h3>
                    <div className="grid gap-4">
                      <FormField
                        control={demographicsForm.control}
                        name="heardAboutUs"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>How did you hear about us?</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an option" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {HEARD_ABOUT_US_OPTIONS.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={demographicsForm.control}
                        name="heardAboutUsDescription"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Additional Details (Optional)</FormLabel>
                            <FormControl>
                              <Textarea
                                {...field}
                                placeholder="Please provide any additional details..."
                                rows={3}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end pt-2">
                    <Button type="submit" disabled={updateDemographicsMutation.isPending} size="lg">
                      {updateDemographicsMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Update Demographics
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        )}

        {/* Insurance Card */}
        {hasPatientLoaded && insuranceQuery.data && (
          <Card className="border-border/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">Patient Insurance</CardTitle>
              <CardDescription>
                Update insurance policy and group numbers (other fields are read-only)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...insuranceForm}>
                <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
                  {insuranceForm.watch('cases').map((insuranceCase, index) => (
                    <div key={insuranceCase.caseId} className="rounded-lg border p-4">
                      <div className="mb-4 flex items-center justify-between">
                        <Badge
                          className={cn(
                            insuranceCase.type === 'Primary' && 'bg-blue-600',
                            insuranceCase.type === 'Secondary' && 'bg-purple-600',
                            insuranceCase.type === 'Tertiary' && 'bg-orange-600'
                          )}
                        >
                          {insuranceCase.type}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          Case ID: {insuranceCase.caseId}
                        </span>
                      </div>

                      {/* Read-Only Provider Information */}
                      <div className="mb-4 rounded-md bg-muted/30 p-3">
                        <h4 className="mb-2 text-sm font-semibold">Provider Information</h4>
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <p>
                            <span className="font-medium">Name:</span>{' '}
                            {insuranceCase.provider.displayName}
                          </p>
                          <p>
                            <span className="font-medium">Address:</span>{' '}
                            {insuranceCase.provider.address}
                          </p>
                          <p>
                            <span className="font-medium">Phone:</span>{' '}
                            {insuranceCase.provider.phone}
                          </p>
                          {insuranceCase.copay && (
                            <p>
                              <span className="font-medium">Copay:</span> {insuranceCase.copay}
                            </p>
                          )}
                          {insuranceCase.policyHolderFirstName && (
                            <p>
                              <span className="font-medium">Policy Holder:</span>{' '}
                              {insuranceCase.policyHolderFirstName}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Editable Fields */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-muted-foreground">
                          Editable Fields
                        </h4>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <FormField
                            control={insuranceForm.control}
                            name={`cases.${index}.policyNumber`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Policy Number</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={insuranceForm.control}
                            name={`cases.${index}.groupNumber`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Group Number</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      {/* Read-Only Fields (Required for Update) */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-muted-foreground">
                          Additional Information (Read-Only)
                        </h4>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <FormField
                            control={insuranceForm.control}
                            name={`cases.${index}.copay`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Copay</FormLabel>
                                <FormControl>
                                  <Input {...field} readOnly className="bg-muted" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={insuranceForm.control}
                            name={`cases.${index}.insuranceRecordId`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Insurance Record ID</FormLabel>
                                <FormControl>
                                  <Input {...field} readOnly className="bg-muted" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={insuranceForm.control}
                            name={`cases.${index}.provider.ehrId`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Provider EHR ID</FormLabel>
                                <FormControl>
                                  <Input {...field} readOnly className="bg-muted" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {insuranceForm.watch('cases').length === 0 && (
                    <div className="flex items-center gap-2 rounded-md bg-yellow-50 px-3 py-2 text-sm text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300">
                      <AlertCircle className="h-4 w-4" />
                      <span>No insurance cases found for this patient</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  {insuranceForm.watch('cases').length > 0 && (
                    <div className="flex justify-end pt-2">
                      <Button
                        type="button"
                        onClick={onUpdateInsurance}
                        disabled={updateInsuranceMutation.isPending}
                        size="lg"
                      >
                        {updateInsuranceMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Update Insurance
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </form>
              </Form>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminPageLayout>
  );
}
